import React from "react";
import PropTypes from "prop-types";
import { gql, useQuery } from "@apollo/client";
import classNames from "classnames";
import { parameterize } from "util/strings";
import Block from "components/Skeleton/Block";

import { Layer, Source } from "@vis.gl/react-maplibre";
import styles from "./style.module.scss";
import BaseMap, {
  FillPaintOptions,
  LinePaintOptions,
} from "../GoogleMap/BaseMap";

const LOCATION_NEIGHBORHOOD_QUERIES = gql`
  query LocationNeighborhoodsQuery($city: String!, $state: String) {
    cities(count: 12) {
      id
      name
      state
      slug
    }

    city(name: $city, state: $state) {
      id
      position
      name
      state
      slug
      neighborhoods {
        id
        name
        geojson
        yearlyPricePerFoot
      }
    }
  }
`;

const LocationSelector = ({ ...props }) => {
  const { data, loading } = useQuery(LOCATION_NEIGHBORHOOD_QUERIES, {
    variables: {
      city: props.city,
      state: props.state,
    },
  });

  if (loading)
    return (
      <div className={styles.loading}>
        <Block className={styles.neighborhoodList} />
        <Block className={styles.map} />
      </div>
    );

  const onChangeNeighborhoods = (evt) => {
    let nextNeighborhoods;
    if (evt.target.checked) {
      // Find checked item by name from Apollo data
      const neighborhood = data.city.neighborhoods.find(
        (n) => parameterize(n.name) === parameterize(evt.target.value)
      );
      if (neighborhood)
        nextNeighborhoods = props.neighborhoods.concat([neighborhood]);
    } else {
      nextNeighborhoods = props.neighborhoods.filter(
        (n) => parameterize(n.name) !== parameterize(evt.target.value)
      );
    }
    if (nextNeighborhoods) props.onChange({ neighborhoods: nextNeighborhoods });
  };

  const onChangeCity = (evt) => {
    const city = data.cities.find((c) => c.name === evt.target.value);
    props.onChange({ city: city.name, state: city.state });
  };
  const neighborhoods = data.city ? data.city.neighborhoods : [];
  const hasNeighborhoods = neighborhoods.length > 0;
  const neighborhoodSelectorClassName = classNames(
    "neighborhoodSelector",
    "checkboxes",
    styles.neighborhoodList,
    props.listClassName
  );
  const mapClassNames = classNames(styles.map, props.mapClassName, {
    [styles.hideMap]: props.hideMobileMap,
  });

  const cities = data.cities ? [...data.cities] : [];
  if (data.city && !cities.find((city) => data.city.id === city.id)) {
    cities.push(data.city);
  }

  return (
    <div className={props.className}>
      {hasNeighborhoods && !props.hideMap && (
        <BaseMap className={mapClassNames} autoCenter>
          {neighborhoods
            .filter(
              (nhood) =>
                props.neighborhoods.findIndex(
                  (n) => parameterize(n.name) === parameterize(nhood.name)
                ) > -1
            )
            .map((b) => (
              <Source key={b.name} id={b.name} type="geojson" data={b.geojson}>
                <Layer
                  id={`${b.name}-fill`}
                  type="fill"
                  paint={FillPaintOptions}
                />
                <Layer
                  id={`${b.name}-line`}
                  type="line"
                  paint={LinePaintOptions}
                />
              </Source>
            ))}
        </BaseMap>
      )}

      <div
        className={classNames("select", styles.cityDropdown, {
          [styles.fullWidth]: props.hideMap,
        })}
      >
        <select name="city" value={props.city} onChange={onChangeCity}>
          <option value="" default disabled>
            Select a city
          </option>
          {cities &&
            cities.map((city) => (
              <option
                key={city.slug}
                value={city.name}
                disabled={props.disableSelections}
              >
                {city.name}, {city.state}
              </option>
            ))}
        </select>
      </div>

      {hasNeighborhoods && (
        <ul className={neighborhoodSelectorClassName}>
          {neighborhoods.map((nhood) => (
            <li key={nhood.id}>
              <label
                htmlFor={`toggle-${nhood.id}`}
                className={classNames({
                  [styles.disabled]: props.disableSelections,
                })}
              >
                <input
                  type="checkbox"
                  id={`toggle-${nhood.id}`}
                  name="neighborhoods"
                  value={nhood.name}
                  checked={
                    props.neighborhoods.findIndex(
                      (n) => parameterize(n.name) === parameterize(nhood.name)
                    ) > -1
                  }
                  onChange={onChangeNeighborhoods}
                  disabled={props.disableSelections}
                />
                {nhood.name}
                {nhood.yearlyPricePerFoot && (
                  <abbr>
                    ${nhood.yearlyPricePerFoot > 60 && "$"}
                    {nhood.yearlyPricePerFoot > 70 && "$"}
                  </abbr>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

LocationSelector.propTypes = {
  city: PropTypes.string,
  state: PropTypes.string,
  neighborhoods: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  mapClassName: PropTypes.string,
  listClassName: PropTypes.string,
  additionalMapOptions: PropTypes.shape({}),
  hideMap: PropTypes.bool,
  hideMobileMap: PropTypes.bool,
  disableSelections: PropTypes.bool,
  onChange: PropTypes.func,
};

LocationSelector.defaultProps = {
  city: "New York",
  state: "NY",
  neighborhoods: [],
  className: "",
  mapClassName: "",
  listClassName: "",
  additionalMapOptions: {},
  hideMap: false,
  hideMobileMap: false,
  disableSelections: false,
  onChange: () => {},
};

export default LocationSelector;
